
import React, { useState } from 'react';
import ModelSelector from './ModelSelector';

/**
 * Add Provider Modal
 * Allows adding new custom AI providers
 */
const AddProviderModal = ({ onSave, onClose, existingProviders = [] }) => {
  const [formData, setFormData] = useState({
    providerName: '',
    displayName: '',
    providerType: 'openai', // openai, gemini, anthropic, custom
    model: '',
    baseUrl: '',
    apiKeyEnvVar: '',
    enabled: true,
    priority: (existingProviders.length || 0) + 1,
    pricing: {
      inputCostPerMillion: 0,
      outputCostPerMillion: 0,
      currency: 'USD'
    },
    rateLimit: {
      requestsPerMinute: 60,
      tokensPerMinute: 100000,
      requestsPerDay: 10000
    }
  });

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const providerTypes = [
    { value: 'openrouter', label: 'OpenRouter (200+ Models)', description: 'Access 200+ models from multiple providers with search' },
    { value: 'openai', label: 'OpenAI Compatible', description: 'Works with OpenAI, DeepSeek, Groq, and most LLM APIs' },
    { value: 'gemini', label: 'Google Gemini', description: 'Google Gemini API format' },
    { value: 'anthropic', label: 'Anthropic Claude', description: 'Anthropic Claude API format' },
    { value: 'custom', label: 'Custom Provider', description: 'Custom API implementation' }
  ];

  const handleModelSelect = (modelId, pricing) => {
    handleChange('model', modelId);

    // Auto-update pricing from model data if available
    if (pricing) {
      setFormData(prev => ({
        ...prev,
        pricing: {
          inputCostPerMillion: pricing.prompt || 0,
          outputCostPerMillion: pricing.completion || 0,
          currency: 'USD'
        }
      }));
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Auto-generate provider name from display name
    if (field === 'displayName' && !formData.providerName) {
      const generatedName = value.toLowerCase().replace(/[^a-z0-9]/g, '-').replace(/-+/g, '-').trim();
      setFormData(prev => ({
        ...prev,
        providerName: generatedName
      }));
    }
  };

  const handleNestedChange = (parent, field, value) => {
    setFormData(prev => ({
      ...prev,
      [parent]: {
        ...prev[parent],
        [field]: value
      }
    }));
  };

  const validateForm = () => {
    if (!formData.providerName) {
      setError('Provider name is required');
      return false;
    }

    if (!formData.displayName) {
      setError('Display name is required');
      return false;
    }

    if (!formData.model) {
      setError('Model name is required');
      return false;
    }

    // Check if provider name already exists
    if (existingProviders.some(p => p.name === formData.providerName)) {
      setError('Provider name already exists. Please use a unique name.');
      return false;
    }

    // Validate provider name format
    if (!/^[a-z0-9-]+$/.test(formData.providerName)) {
      setError('Provider name must contain only lowercase letters, numbers, and hyphens');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (!validateForm()) {
      return;
    }

    setSaving(true);

    try {
      const baseUrl = formData.providerType === 'openrouter'
        ? 'https://openrouter.ai/api/v1'
        : formData.baseUrl;

      await onSave({
        provider_name: formData.providerName,
        display_name: formData.displayName,
        model_name: formData.model,
        base_url: baseUrl || null,
        api_key_env_var: `REACT_APP_${formData.providerName.toUpperCase()}_API_KEY`,
        enabled: formData.enabled,
        priority: formData.priority,
        configuration: {
          providerType: formData.providerType,
          baseUrl: baseUrl,
          apiKeyEnvVar: `REACT_APP_${formData.providerName.toUpperCase()}_API_KEY`
        },
        capabilities: {
          cvExtraction: true,
          immigrationAnalysis: true,
          streaming: formData.providerType === 'openai',
          jsonMode: formData.providerType === 'openai'
        },
        pricing: formData.pricing,
        rate_limits: formData.rateLimit
      });
      onClose();
    } catch (err) {
      setError(err.message || 'Failed to add provider');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4 font-mono">
      <div className="bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto border border-gray-700">
        {/* Header */}
        <div className="sticky top-0 bg-gray-800 border-b border-gray-700 px-6 py-4 flex items-center justify-between z-10">
          <div>
            <h2 className="text-xl font-semibold text-white">Add Custom AI Provider</h2>
            <p className="text-sm text-gray-400 mt-1">Configure a new AI provider for CV analysis</p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-200"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Error Message */}
          {error && (
            <div className="bg-red-900/20 border border-red-800 rounded-lg p-4">
              <div className="flex items-center gap-2">
                <svg className="w-5 h-5 text-red-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
                <span className="text-red-400">{error}</span>
              </div>
            </div>
          )}

          {/* Provider Type */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-white">Provider Type</h3>

            <div className="grid grid-cols-1 gap-3">
              {providerTypes.map((type) => (
                <label
                  key={type.value}
                  className={`relative flex cursor-pointer rounded-lg border p-4 focus:outline-none ${formData.providerType === type.value
                      ? 'border-blue-500 bg-blue-900/20'
                      : 'border-gray-600 bg-gray-700 hover:bg-gray-600'
                    }`}
                >
                  <input
                    type="radio"
                    name="providerType"
                    value={type.value}
                    checked={formData.providerType === type.value}
                    onChange={(e) => handleChange('providerType', e.target.value)}
                    className="sr-only"
                  />
                  <div className="flex flex-1">
                    <div className="flex flex-col">
                      <span className={`block text-sm font-medium ${formData.providerType === type.value ? 'text-blue-400' : 'text-white'
                        }`}>
                        {type.label}
                      </span>
                      <span className={`mt-1 flex items-center text-sm ${formData.providerType === type.value ? 'text-blue-300' : 'text-gray-400'
                        }`}>
                        {type.description}
                      </span>
                    </div>
                  </div>
                  {formData.providerType === type.value && (
                    <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                  )}
                </label>
              ))}
            </div>
          </div>

          {/* Basic Information */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-white">Basic Information</h3>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">
                Display Name *
              </label>
              <input
                type="text"
                value={formData.displayName}
                onChange={(e) => handleChange('displayName', e.target.value)}
                className="w-full px-3 py-2 border border-gray-600 bg-gray-700 text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent placeholder-gray-500"
                placeholder="My Custom AI Provider"
                required
              />
              <p className="text-xs text-gray-500 mt-1">
                Friendly name shown in the UI
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">
                Provider Name (Identifier) *
              </label>
              <input
                type="text"
                value={formData.providerName}
                onChange={(e) => handleChange('providerName', e.target.value)}
                className="w-full px-3 py-2 border border-gray-600 bg-gray-700 text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono text-sm placeholder-gray-500"
                placeholder="my-custom-provider"
                pattern="[a-z0-9-]+"
                required
              />
              <p className="text-xs text-gray-500 mt-1">
                Unique identifier (lowercase, numbers, hyphens only)
              </p>
            </div>

            <div>
              <ModelSelector
                value={formData.model}
                onChange={handleModelSelect}
                apiKey={process.env.REACT_APP_OPENROUTER_API_KEY || process.env.REACT_APP_PROVIDER_API_KEY}
                providerType={formData.providerType}
              />
            </div>

            {formData.providerType !== 'openrouter' && (
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  API Base URL *
                </label>
                <input
                  type="url"
                  value={formData.baseUrl}
                  onChange={(e) => handleChange('baseUrl', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-600 bg-gray-700 text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono text-sm placeholder-gray-500"
                  placeholder="https://api.provider.com/v1"
                  required={formData.providerType !== 'openrouter'}
                />
                <p className="text-xs text-gray-500 mt-1">
                  Full API endpoint URL
                </p>
              </div>
            )}

            {formData.providerType === 'openrouter' && (
              <div className="bg-blue-900/20 border border-blue-800 rounded-lg p-3">
                <p className="text-sm text-blue-400">
                  <strong>OpenRouter:</strong> API base URL is automatically set to https://openrouter.ai/api/v1
                </p>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Priority
                </label>
                <input
                  type="number"
                  min="1"
                  value={formData.priority}
                  onChange={(e) => handleChange('priority', parseInt(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-600 bg-gray-700 text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Lower = higher priority
                </p>
              </div>

              <div className="flex items-end">
                <label className="flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.enabled}
                    onChange={(e) => handleChange('enabled', e.target.checked)}
                    className="w-4 h-4 text-blue-500 border-gray-600 rounded focus:ring-blue-500 bg-gray-700"
                  />
                  <span className="ml-2 text-sm font-medium text-gray-300">
                    Enable provider
                  </span>
                </label>
              </div>
            </div>
          </div>

          {/* Pricing */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-white">Pricing (Optional)</h3>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Input Cost (per 1M tokens)
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-2 text-gray-500">$</span>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.pricing.inputCostPerMillion}
                    onChange={(e) => handleNestedChange('pricing', 'inputCostPerMillion', parseFloat(e.target.value))}
                    className="w-full pl-8 pr-3 py-2 border border-gray-600 bg-gray-700 text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Output Cost (per 1M tokens)
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-2 text-gray-500">$</span>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.pricing.outputCostPerMillion}
                    onChange={(e) => handleNestedChange('pricing', 'outputCostPerMillion', parseFloat(e.target.value))}
                    className="w-full pl-8 pr-3 py-2 border border-gray-600 bg-gray-700 text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Rate Limits */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-white">Rate Limits (Optional)</h3>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Requests/Min
                </label>
                <input
                  type="number"
                  min="1"
                  value={formData.rateLimit.requestsPerMinute}
                  onChange={(e) => handleNestedChange('rateLimit', 'requestsPerMinute', parseInt(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-600 bg-gray-700 text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Tokens/Min
                </label>
                <input
                  type="number"
                  min="1"
                  value={formData.rateLimit.tokensPerMinute}
                  onChange={(e) => handleNestedChange('rateLimit', 'tokensPerMinute', parseInt(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-600 bg-gray-700 text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Requests/Day
                </label>
                <input
                  type="number"
                  min="1"
                  value={formData.rateLimit.requestsPerDay}
                  onChange={(e) => handleNestedChange('rateLimit', 'requestsPerDay', parseInt(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-600 bg-gray-700 text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>
          </div>

          {/* API Key Info */}
          <div className="bg-blue-900/20 border border-blue-800 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <svg className="w-5 h-5 text-blue-400 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
              <div>
                <h4 className="font-medium text-blue-400 mb-1">API Key Setup</h4>
                <p className="text-sm text-blue-300 mb-2">
                  After adding this provider, configure the API key in your <code className="bg-blue-900/50 px-1 py-0.5 rounded border border-blue-800">.env</code> file:
                </p>
                <pre className="bg-gray-900 px-3 py-2 rounded text-xs font-mono text-blue-300 border border-gray-700 overflow-x-auto">
                  {`REACT_APP_${formData.providerName.toUpperCase() || 'PROVIDER'}_API_KEY=your_api_key_here`}
                </pre>
                <p className="text-xs text-blue-400 mt-2">
                  Restart the application after updating environment variables.
                </p>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-700">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-white bg-gray-700 border border-gray-600 rounded-lg hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              disabled={saving}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
              disabled={saving}
            >
              {saving ? (
                <span className="flex items-center gap-2">
                  <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Adding Provider...
                </span>
              ) : (
                'Add Provider'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddProviderModal;
